import React, { cloneElement, useState } from 'react';
import { usePopper } from 'react-popper';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Portal from '../../layout/Portal/Portal';
import Icon from '../icon/Icon';

export const UserPopovers = ({ name, username, department, division, position, ...props }) => {
	return (
		<Popovers
			desc={
				<table className={classNames('table', 'table-sm', 'table-borderless')}>
					<tbody>
						<tr>
							<td>Name</td>
							<td>: {name}</td>
						</tr>
						<tr>
							<td>Employee ID</td>
							<td>: {username}</td>
						</tr>
						<tr>
							<td>Department</td>
							<td>: {department}</td>
						</tr>
						<tr>
							<td>Division</td>
							<td>: {division}</td>
						</tr>
						<tr>
							<td>Position</td>
							<td>: {position}</td>
						</tr>
					</tbody>
				</table>
			}
			trigger='hover'
			style={{ width: '350px' }}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}>
			<div>
				<Icon icon='Person' color='info' /> {name}
			</div>
		</Popovers>
	);
};

UserPopovers.propTypes = {
	name: PropTypes.string,
	username: PropTypes.string,
	department: PropTypes.string,
	division: PropTypes.string,
	position: PropTypes.string,
};
UserPopovers.defaultProps = {
	name: '-',
	username: '-',
	department: '-',
	division: '-',
	position: '-',
};

export const CriteriaDetailPopovers = ({ data }) => {
	if (data?.length === 0) {
		return null;
	}

	return (
		<Popovers
			title='Point Criteria'
			trigger='click'
			style={{ width: '800px' }}
			desc={
				<table className={classNames('table', 'table-modern')}>
					<thead>
						<tr>
							<th>Criteria</th>
							<th>Point</th>
							<th>Description</th>
						</tr>
					</thead>
					<tbody>
						{data?.map((item, index) => (
							<tr key={'row-popover'.concat(index)}>
								<td>{item?.name}</td>
								<td>{item?.point_value}</td>
								<td>{item?.description}</td>
							</tr>
						))}
					</tbody>
				</table>
			}>
			<Icon icon='info' color='info' />
		</Popovers>
	);
};

CriteriaDetailPopovers.propTypes = {
	data: PropTypes.instanceOf(Array),
};
CriteriaDetailPopovers.defaultProps = {
	data: [],
};

const Popovers = ({
	children,
	className,
	bodyClassName,
	title,
	desc,
	placement,
	flip,
	trigger,
	delay,
	isDisplayInline,
	modifiers,
	style,
	...props
}) => {
	const [referenceElement, setReferenceElement] = useState(null);
	const [popperElement, setPopperElement] = useState(null);
	const [arrowElement, setArrowElement] = useState(null);
	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		placement,
		modifiers: [
			{
				name: 'offset',
				options: {
					offset: [0, 6.5],
				},
			},
			{
				name: 'flip',
				enabled: true,
				options: {
					fallbackPlacements: flip,
				},
			},
			{
				name: 'arrow',
				options: {
					element: arrowElement,
				},
			},
			{ ...modifiers },
		],
	});

	const [popoverOpen, setPopoverOpen] = useState(false);

	const _onClick = () => {
		if (trigger === 'click') setPopoverOpen(!popoverOpen);
		if (children?.props?.onClick) children.props.onClick();
	};

	const _onMouseOver = () => {
		if (trigger === 'hover') setPopoverOpen(true);
		if (children?.props?.onMouseOver) children.props.onMouseOver();
	};

	const _onMouseLeave = () => {
		if (trigger === 'hover') setTimeout(() => setPopoverOpen(false), delay);
		if (children?.props?.onMouseLeave) children.props.onMouseLeave();
	};

	const _props = {
		className: classNames(
			{ 'd-inline-block': isDisplayInline, 'popover-string': typeof children === 'string' },
			children?.props?.className,
		),
		onClick: _onClick,
		onMouseOver: _onMouseOver,
		onMouseLeave: _onMouseLeave,
	};

	return (
		<>
			{cloneElement(
				typeof children === 'string' ? (
					// eslint-disable-next-line react/jsx-props-no-spreading
					<span ref={setReferenceElement} {..._props}>
						{children}
					</span>
				) : (
					children
				),
				{
					ref: setReferenceElement,
					..._props,
				},
			)}
			{popoverOpen && (
				<Portal>
					<div
						ref={setPopperElement}
						role='tooltip'
						className={classNames('popover', 'bs-popover-auto', className)}
						style={{ ...styles.popper, ...style }}
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...props}
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...attributes.popper}>
						<div ref={setArrowElement} className='popover-arrow' style={styles.arrow} />
						{title && <h3 className='popover-header'>{title}</h3>}
						{desc && (
							<div className={classNames('popover-body', bodyClassName)}>{desc}</div>
						)}
					</div>
				</Portal>
			)}
		</>
	);
};
Popovers.propTypes = {
	/**
	 * String, HTML or React Component (`<Component {...props} />`)
	 */
	children: PropTypes.node.isRequired,
	title: PropTypes.string,
	/**
	 * String, HTML or React Component
	 */
	desc: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	/**
	 * Position of popovers
	 */
	placement: PropTypes.oneOf([
		'auto',
		'auto-start',
		'auto-end',
		'top',
		'top-start',
		'top-end',
		'bottom',
		'bottom-start',
		'bottom-end',
		'right',
		'right-start',
		'right-end',
		'left',
		'left-start',
		'left-end',
	]),
	/**
	 * For situations where there is not enough space to place
	 */
	flip: PropTypes.arrayOf(
		PropTypes.oneOf([
			'auto',
			'auto-start',
			'auto-end',
			'top',
			'top-start',
			'top-end',
			'bottom',
			'bottom-start',
			'bottom-end',
			'right',
			'right-start',
			'right-end',
			'left',
			'left-start',
			'left-end',
		]),
	),
	trigger: PropTypes.oneOf(['click', 'hover']),
	/**
	 * The value entered is in milliseconds
	 */
	delay: PropTypes.number,
	/**
	 * Adds style: `display: inline-block;`
	 */
	isDisplayInline: PropTypes.bool,
	className: PropTypes.string,
	bodyClassName: PropTypes.string,
	/**
	 * More information, [Popper.js](https://popper.js.org/docs/v2/modifiers/)
	 */
	// eslint-disable-next-line react/forbid-prop-types
	modifiers: PropTypes.object,
	style: PropTypes.instanceOf(Object),
};
Popovers.defaultProps = {
	title: null,
	desc: null,
	placement: 'top',
	flip: ['top', 'bottom'],
	trigger: 'click',
	delay: 0,
	isDisplayInline: false,
	className: null,
	bodyClassName: null,
	modifiers: {
		name: 'example',
		enabled: false,
		phase: 'read',
		fn: () => {},
	},
	style: null,
};

export default Popovers;
